import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAtom } from 'jotai/react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Flex } from '@/components/common/Box'
import { Button, OutlineButton } from '@/components/common/Button'
import { ProDialog } from '@/components/common/Dialog'
import { NumberInput } from '@/components/common/Input'
import { RectSwitch } from '@/components/common/Switch'
import { OrbitronText } from '@/components/common/Text'
import { Form, FormValidField } from '@/components/create/FormComp'
import {
  DEFAULT_MEV_STATE,
  DEFAULT_SLIPPAGE_VALUE,
  SLIPPAGE_OPTIONS,
  SUPPORT_RPC_ENDPOINT
} from '@/constants/settings'
import { filterPositive } from '@/lib/format'
import { cn } from '@/lib/utils'
import { rpcSettingAtom, transactionSettingAtom } from '@/stores/settings'

const PopupSlippage: React.FC<{
  open: boolean
  onOpenChange: (state: boolean) => void
}> = ({ open, onOpenChange }) => {
  const { t } = useTranslation()
  const [txSetting, setTxSetting] = useAtom(transactionSettingAtom)
  // const [rpcEndpoint] = useAtom(rpcEndpointAtom)
  const [rpcSetting] = useAtom(rpcSettingAtom)

  const formSchema = z.object({
    slippage: z
      .string()
      .min(1, t('coin.trade.messageSlippageRequired'))
      .refine(
        (val) => {
          return Number(val) > 0 && Number(val) <= 50
        },
        t('coin.trade.messageSlippageLimit', { var1: 0, var2: 50 })
      ),
    mevState: z.boolean()
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema)
    // defaultValues: {
    //   slippage: priorityFeeSetting.slippage ?? DEFAULT_SLIPPAGE_VALUE,
    //   mevState: priorityFeeSetting.mevState ?? DEFAULT_MEV_STATE,
    // }
  })

  const slippageCurrent = form.watch('slippage')
  const mevStateCurrent = form.watch('mevState')

  const memoChanged = useMemo(() => {
    return (
      txSetting.slippage !== slippageCurrent ||
      txSetting.mevState !== mevStateCurrent
    )
  }, [txSetting, mevStateCurrent, slippageCurrent])

  const memoMevUseable = useMemo(() => {
    const rpcCurrent = SUPPORT_RPC_ENDPOINT.find(
      (item) => item.key === rpcSetting.rpcKey
    )
    return !!rpcCurrent?.mev
  }, [rpcSetting])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const formData = values
    const flag = Object.keys(formData).some((key: string) => {
      return (formData as any)[key] !== (txSetting as any)[key]
    })
    if (flag) {
      setTxSetting({
        ...txSetting,
        ...formData
      })
    }
    if (onOpenChange) {
      onOpenChange(false)
    }
  }

  const onChangeSlippage = (value: string) => {
    let val = filterPositive(value, 2)
    val = Number(val) > 50 ? '50' : val
    form.setValue('slippage', String(val))
  }

  useEffect(() => {
    if (open) {
      form.reset({
        slippage: txSetting.slippage ?? DEFAULT_SLIPPAGE_VALUE,
        mevState: txSetting.mevState ?? DEFAULT_MEV_STATE
      })
    }
  }, [form, txSetting, open])

  return (
    <ProDialog
      open={open}
      title={t('coin.trade.setSlippage')}
      onOpenChange={onOpenChange}
      contentProps={{ className: 'xl:max-w-[480px]' }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} data-testid="slippageForm">
        <Form {...form}>
          <div className="space-y-3 xl:space-y-4">
            <FormValidField
              control={form.control}
              name="mevState"
              controlRender={({ field }) => (
                <Flex className="items-center justify-between">
                  <OrbitronText className="text-sm xl:text-base">
                    {t('coin.trade.mevlabel')}
                  </OrbitronText>
                  <RectSwitch
                    disabled={!memoMevUseable}
                    checked={memoMevUseable && field.value}
                    onCheckedChange={field.onChange}
                  />
                </Flex>
              )}
            />

            <Flex className="justify-between gap-3">
              {SLIPPAGE_OPTIONS.map((item, index) => (
                <Button
                  key={`${index}-${item}`}
                  size="sm"
                  outline
                  className={cn(
                    'font-Orbitron flex-1',
                    slippageCurrent === item && 'border-primary text-primary'
                  )}
                  variant={'primary'}
                  onClick={() => {
                    onChangeSlippage(item)
                  }}
                >
                  {item}%
                </Button>
              ))}
            </Flex>
            <FormValidField
              control={form.control}
              name="slippage"
              controlRender={({ field }) => (
                <NumberInput
                  {...field}
                  size="md"
                  className="font-Orbitron"
                  maxLength={5}
                  suffixNode={
                    <OrbitronText className="text-sm xl:text-xl">
                      %
                    </OrbitronText>
                  }
                  onChange={(event) => {
                    onChangeSlippage(event.target.value)
                  }}
                />
              )}
            />
            <Flex
              className={cn(
                'justify-between gap-3 xl:gap-4',
                '[&_button]:font-Orbitron [&_button]:h-12 [&_button]:flex-1 [&_button]:text-base xl:[&_button]:text-lg'
              )}
            >
              <OutlineButton onClick={() => form.reset()}>
                {t('coin.trade.reset')}
              </OutlineButton>
              <Button type="submit" border={false} disabled={!memoChanged}>
                {t('common.confirm')}
              </Button>
            </Flex>
          </div>
        </Form>
      </form>
    </ProDialog>
  )
}

export default PopupSlippage
